import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Mail, Send, Clock, ChevronDown, Globe, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { successToast, errorToast } from "@/components/ui/sonner";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";




const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } 
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};


export default function Contact() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    requestType: "general",
    message: ""
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIndicator(window.scrollY < 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const requestLabel = "General Inquiry";
      
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            company: formData.company,
            requestType: requestLabel,
            message: formData.message,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to send");

      setIsSubmitted(true);
    } catch (err) {
      console.error("Contact form error:", err);
      errorToast("Something went wrong", "Please try again or email us directly at hello@sorvaine.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  

  return (
    <PublicPageLayout>
      <SEO 
        title="Contact Sorvaine | Request a Demo or Briefing"
        description="Get in touch with Sorvaine. Request a demo, schedule a briefing, or learn how our proposal intelligence platform can transform your win rate."
        canonical="https://sorvaine.com/contact"
      />

      {/* Hero Section */}
      <section className="pt-20 pb-12 md:pt-32 md:pb-20 relative animate-hero-gradient min-h-[40vh] md:min-h-[60vh] flex flex-col justify-center">
        <div className="absolute inset-0 geometric-grid opacity-20 pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            {/* Decorated label */}
            <motion.div variants={fadeIn} className="flex items-center gap-4 mb-6 md:mb-10">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <div className="w-10 h-px bg-gradient-to-r from-primary to-transparent" />
              </div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium">Contact</span>
            </motion.div>

            <h1 className="font-serif text-[30px] sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6 md:mb-8 whitespace-nowrap">
              Let's start a conversation
            </h1>

            <motion.p 
              variants={fadeIn}
              className="text-muted-foreground text-base md:text-xl leading-relaxed max-w-[65ch]"
            >
              Whether you're ready to get started or just have questions, we're here.
            </motion.p>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <AnimatePresence>
          {showScrollIndicator && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60">Scroll</span>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ChevronDown className="w-4 h-4 text-muted-foreground/40" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Glowing Divider */}
      <div className="divider-glow" />

      {/* Main Content - Form & Contact Info */}
      <section className="py-12 md:py-28 relative">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-20">
            
            {/* Form Section */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="lg:col-span-7"
            >
              <div className="landing-card rounded-2xl p-6 md:p-10 !bg-[hsl(0_0%_11%)]">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center text-center py-10 md:py-16 space-y-5 md:space-y-6"
                  >
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl">Thanks for reaching out.</h2>
                    <p className="text-muted-foreground text-base max-w-md">
                      We'll get back to you within 24 hours.
                    </p>
                  </motion.div>
                ) : (
                  <>
                    <div className="mb-6 md:mb-8">
                      <h2 className="font-serif text-xl md:text-3xl mb-2 md:mb-3">Send us a message</h2>
                      <p className="text-muted-foreground text-sm">Fill out the form and we'll respond within 24 hours.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <label className="text-[11px] tracking-[0.15em] uppercase text-foreground/80 font-medium">
                            Name
                          </label>
                          <Input
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="h-12 !bg-[hsl(0_0%_18%)] border-white/15 text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 rounded-lg"
                            placeholder="Your name"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[11px] tracking-[0.15em] uppercase text-foreground/80 font-medium">
                            Email
                          </label>
                          <Input
                            required
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="h-12 !bg-[hsl(0_0%_18%)] border-white/15 text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 rounded-lg"
                            placeholder="you@company.com"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-[11px] tracking-[0.15em] uppercase text-foreground/80 font-medium">
                          Company
                        </label>
                        <Input
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="h-12 !bg-[hsl(0_0%_18%)] border-white/15 text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 rounded-lg"
                          placeholder="Your company"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-[11px] tracking-[0.15em] uppercase text-foreground/80 font-medium">
                          Message
                        </label>
                        <Textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="min-h-[200px] !bg-[hsl(0_0%_18%)] border-white/15 text-foreground placeholder:text-muted-foreground/70 focus:border-primary/60 resize-none rounded-lg"
                          placeholder="What are you working on?"
                        />
                      </div>
                      
                      <button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full h-12 md:h-14 rounded-full bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <Send className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>

            {/* Contact Info Sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="lg:col-span-5 space-y-6 md:space-y-8"
            >
              {/* Quick Contact */}
              <div className="space-y-5 md:space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                  <h3 className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">Quick Contact</h3>
                </div>
                
                <a 
                  href="mailto:hello@sorvaine.com"
                  className="landing-card group flex items-start gap-4 p-5 rounded-xl hover:border-primary/30 transition-all duration-300 !bg-[hsl(0_0%_11%)]"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Email</p>
                    <p className="text-foreground font-medium group-hover:text-primary transition-colors">hello@sorvaine.com</p>
                  </div>
                </a>

                <div className="landing-card flex items-start gap-4 p-5 rounded-xl !bg-[hsl(0_0%_11%)]">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Response Time</p>
                    <p className="text-foreground font-medium">Within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Remote-First */}
              <div className="space-y-4 md:space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                  <h3 className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">Location</h3>
                </div>
                
                <div className="landing-card flex items-start gap-4 p-5 rounded-xl !bg-[hsl(0_0%_11%)]">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Globe className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Remote-First</p>
                    <p className="text-foreground font-medium">Headquartered in Dubai, UAE. Serving bid and proposal teams worldwide.</p>
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

    </PublicPageLayout>
  );
}
